import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import {
  fetchCatalogParts,
  createCatalogPart,
  updateCatalogPart,
  deleteCatalogPart,
  importCatalogCSV,
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  fetchWorkOrderParts,
  addWorkOrderPart,
  updateWorkOrderPart,
  deleteWorkOrderPart,
  fetchPurchaseOrders,
  fetchPurchaseOrder,
  createPurchaseOrder,
  sendPurchaseOrder,
  changePOStatus,
  updatePOLogistics,
  deletePurchaseOrder,
  fetchCollectionRuns,
  runCollection,
} from './api'
import type {
  CatalogPartWriteBody,
  PurchaseOrderLogisticsBody,
  PurchaseOrderStatus,
  SupplierWriteBody,
  WorkOrderPartWriteBody,
} from '@/types/parts'

// --- Query key factory (cache contract: list = ["<entity>"], detail = ["<entity>", id]) ---

export const PARTS_KEYS = {
  catalog: (filters?: Record<string, unknown>) => ['parts', { filters }] as const,
  suppliers: ['suppliers'] as const,
  workOrderParts: (workOrderId: string) => ['work-order-parts', workOrderId] as const,
  purchaseOrders: ['purchase-orders'] as const,
  purchaseOrder: (id: string) => ['purchase-orders', id] as const,
  collectionRuns: ['collection-runs'] as const,
}

// --- Outbox keys (see apps/frontend/AGENTS.md "offline outbox") ---

export const PART_CREATE_KEY = ['outbox', 'parts-catalog', 'create'] as const
export const PART_UPDATE_KEY = ['outbox', 'parts-catalog', 'update'] as const
export const PART_DELETE_KEY = ['outbox', 'parts-catalog', 'delete'] as const
export const SUPPLIER_CREATE_KEY = ['outbox', 'parts-suppliers', 'create'] as const
export const SUPPLIER_UPDATE_KEY = ['outbox', 'parts-suppliers', 'update'] as const
export const SUPPLIER_DELETE_KEY = ['outbox', 'parts-suppliers', 'delete'] as const
export const WORK_ORDER_PART_ADD_KEY = ['outbox', 'work-order-parts', 'add'] as const
export const WORK_ORDER_PART_UPDATE_KEY = ['outbox', 'work-order-parts', 'update'] as const
export const WORK_ORDER_PART_DELETE_KEY = ['outbox', 'work-order-parts', 'delete'] as const
export const PO_CREATE_KEY = ['outbox', 'purchase-orders', 'create'] as const
export const PO_SEND_KEY = ['outbox', 'purchase-orders', 'send'] as const
export const PO_STATUS_KEY = ['outbox', 'purchase-orders', 'status'] as const
export const PO_LOGISTICS_KEY = ['outbox', 'purchase-orders', 'logistics'] as const
export const PO_DELETE_KEY = ['outbox', 'purchase-orders', 'delete'] as const
export const COLLECTION_RUN_KEY = ['outbox', 'parts-collection-runs', 'run'] as const

// Replay functions must mirror the hook mutationFn exactly (same imported
// function) so an offline replay behaves identically to an online submit.
// Work-order part / deal-scoped hooks carry their scope id in the variables
// (not a closure) so a persisted replay can rebuild the request path.
queryClient.setMutationDefaults(PART_CREATE_KEY, { mutationFn: createCatalogPart })
queryClient.setMutationDefaults(PART_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: Partial<CatalogPartWriteBody> }) =>
    updateCatalogPart(id, body),
})
queryClient.setMutationDefaults(PART_DELETE_KEY, { mutationFn: deleteCatalogPart })
queryClient.setMutationDefaults(SUPPLIER_CREATE_KEY, { mutationFn: createSupplier })
queryClient.setMutationDefaults(SUPPLIER_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: Partial<SupplierWriteBody> }) =>
    updateSupplier(id, body),
})
queryClient.setMutationDefaults(SUPPLIER_DELETE_KEY, { mutationFn: deleteSupplier })
queryClient.setMutationDefaults(WORK_ORDER_PART_ADD_KEY, {
  mutationFn: ({ workOrderId, body }: { workOrderId: string; body: WorkOrderPartWriteBody }) =>
    addWorkOrderPart(workOrderId, body),
})
queryClient.setMutationDefaults(WORK_ORDER_PART_UPDATE_KEY, {
  mutationFn: ({
    lineId,
    body,
  }: {
    lineId: string
    body: Partial<WorkOrderPartWriteBody>
  }) => updateWorkOrderPart(lineId, body),
})
queryClient.setMutationDefaults(WORK_ORDER_PART_DELETE_KEY, { mutationFn: deleteWorkOrderPart })
queryClient.setMutationDefaults(PO_CREATE_KEY, { mutationFn: createPurchaseOrder })
queryClient.setMutationDefaults(PO_SEND_KEY, {
  mutationFn: ({ id, shipTo }: { id: string; shipTo?: string }) => sendPurchaseOrder(id, shipTo),
})
queryClient.setMutationDefaults(PO_STATUS_KEY, {
  mutationFn: ({ id, status }: { id: string; status: PurchaseOrderStatus }) =>
    changePOStatus(id, status),
})
queryClient.setMutationDefaults(PO_LOGISTICS_KEY, {
  mutationFn: ({ id, body }: { id: string; body: PurchaseOrderLogisticsBody }) =>
    updatePOLogistics(id, body),
})
queryClient.setMutationDefaults(PO_DELETE_KEY, { mutationFn: deletePurchaseOrder })
queryClient.setMutationDefaults(COLLECTION_RUN_KEY, { mutationFn: runCollection })

// --- Catalog ---

export const useCatalogParts = (filters?: {
  q?: string
  manufacturer?: string
  supplier_id?: string
  limit?: number
  offset?: number
}) => {
  return useQuery({
    queryKey: PARTS_KEYS.catalog(filters),
    queryFn: () => fetchCatalogParts(filters),
  })
}

export const useCreateCatalogPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PART_CREATE_KEY,
    mutationFn: createCatalogPart,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

export const useUpdateCatalogPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PART_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: Partial<CatalogPartWriteBody> }) =>
      updateCatalogPart(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

export const useDeleteCatalogPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PART_DELETE_KEY,
    mutationFn: deleteCatalogPart,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

export const useImportCatalogCSV = () => {
  const qc = useQueryClient()
  return useMutation({
    // File payload can't serialize into the persisted outbox — fail fast
    // offline instead of pausing.
    networkMode: 'always',
    mutationFn: (input: { file: File; supplier_id?: string; manufacturer?: string }) =>
      importCatalogCSV(input.file, {
        supplier_id: input.supplier_id,
        manufacturer: input.manufacturer,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['parts'] })
      qc.invalidateQueries({ queryKey: PARTS_KEYS.collectionRuns })
    },
  })
}

// --- Suppliers ---

export const useSuppliers = () => {
  return useQuery({ queryKey: PARTS_KEYS.suppliers, queryFn: () => fetchSuppliers() })
}

export const useCreateSupplier = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: SUPPLIER_CREATE_KEY,
    mutationFn: createSupplier,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.suppliers }),
  })
}

export const useUpdateSupplier = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: SUPPLIER_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: Partial<SupplierWriteBody> }) =>
      updateSupplier(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PARTS_KEYS.suppliers })
      qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders })
    },
  })
}

export const useDeleteSupplier = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: SUPPLIER_DELETE_KEY,
    mutationFn: deleteSupplier,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.suppliers }),
  })
}

// --- Work order part lines ---

export const useWorkOrderParts = (workOrderId: string) => {
  return useQuery({
    queryKey: PARTS_KEYS.workOrderParts(workOrderId),
    queryFn: () => fetchWorkOrderParts(workOrderId),
    enabled: !!workOrderId,
  })
}

// Line changes move the work order's aggregate parts_cost on the backend, so
// every mutation invalidates the work order (detail + lists) too.
const useInvalidatePartsAndWorkOrder = () => {
  const qc = useQueryClient()
  return (workOrderId: string) => {
    qc.invalidateQueries({ queryKey: ['work-order-parts', workOrderId] })
    qc.invalidateQueries({ queryKey: ['work-orders'] })
  }
}

// The workOrderId rides in the mutation variables (injected here, not via a
// closure) so an offline replay can rebuild the request path after a reload.
export const useAddWorkOrderPart = (workOrderId: string) => {
  const invalidate = useInvalidatePartsAndWorkOrder()
  const mutation = useMutation<
    Awaited<ReturnType<typeof addWorkOrderPart>>,
    Error,
    { workOrderId: string; body: WorkOrderPartWriteBody }
  >({
    mutationKey: WORK_ORDER_PART_ADD_KEY,
    mutationFn: ({ workOrderId: woId, body }) => addWorkOrderPart(woId, body),
    onSuccess: (_data, vars) => invalidate(vars.workOrderId),
  })
  return {
    ...mutation,
    mutate: (body: WorkOrderPartWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ workOrderId, body }, options),
    mutateAsync: (
      body: WorkOrderPartWriteBody,
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync({ workOrderId, body }, options),
  }
}

export const useUpdateWorkOrderPart = (workOrderId: string) => {
  const invalidate = useInvalidatePartsAndWorkOrder()
  return useMutation({
    mutationKey: WORK_ORDER_PART_UPDATE_KEY,
    mutationFn: ({ lineId, body }: { lineId: string; body: Partial<WorkOrderPartWriteBody> }) =>
      updateWorkOrderPart(lineId, body),
    onSuccess: () => invalidate(workOrderId),
  })
}

export const useDeleteWorkOrderPart = (workOrderId: string) => {
  const invalidate = useInvalidatePartsAndWorkOrder()
  return useMutation({
    mutationKey: WORK_ORDER_PART_DELETE_KEY,
    mutationFn: deleteWorkOrderPart,
    onSuccess: () => invalidate(workOrderId),
  })
}

// --- Purchase orders ---

export const usePurchaseOrders = (filters?: {
  supplier_id?: string
  work_order_id?: string
  status?: PurchaseOrderStatus
}) => {
  return useQuery({
    queryKey: [...PARTS_KEYS.purchaseOrders, { filters }],
    queryFn: () => fetchPurchaseOrders(filters),
  })
}

export const usePurchaseOrder = (id: string) => {
  return useQuery({
    queryKey: PARTS_KEYS.purchaseOrder(id),
    queryFn: () => fetchPurchaseOrder(id),
    enabled: !!id,
  })
}

export const useCreatePurchaseOrder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PO_CREATE_KEY,
    mutationFn: createPurchaseOrder,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders })
      qc.invalidateQueries({ queryKey: ['work-order-parts'] })
    },
  })
}

export const useSendPurchaseOrder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PO_SEND_KEY,
    mutationFn: ({ id, shipTo }: { id: string; shipTo?: string }) => sendPurchaseOrder(id, shipTo),
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders }),
  })
}

export const useChangePOStatus = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PO_STATUS_KEY,
    mutationFn: ({ id, status }: { id: string; status: PurchaseOrderStatus }) =>
      changePOStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders }),
  })
}

// Logistics edits (ETA / carrier / tracking) show up on the PO detail, the PO
// list, and any work order page listing the PO — the ['purchase-orders']
// prefix invalidates all of them, including ['purchase-orders', id] details.
export const useUpdatePOLogistics = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PO_LOGISTICS_KEY,
    mutationFn: ({ id, body }: { id: string; body: PurchaseOrderLogisticsBody }) =>
      updatePOLogistics(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders }),
  })
}

export const useDeletePurchaseOrder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: PO_DELETE_KEY,
    mutationFn: deletePurchaseOrder,
    onSuccess: () => qc.invalidateQueries({ queryKey: PARTS_KEYS.purchaseOrders }),
  })
}

// --- Price collection ---

export const useCollectionRuns = (supplierId?: string) => {
  return useQuery({
    queryKey: [...PARTS_KEYS.collectionRuns, { supplierId }],
    queryFn: () => fetchCollectionRuns(supplierId),
  })
}

export const useRunCollection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: COLLECTION_RUN_KEY,
    mutationFn: runCollection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PARTS_KEYS.collectionRuns })
      qc.invalidateQueries({ queryKey: ['parts'] })
    },
  })
}
